/**
 * Event dates and times, resolved the way the legacy tool resolved them.
 *
 * Every BCE event happens in a city, and that city's wall clock — not the
 * viewer's — is the one the client, the venue and the facilitator agreed on.
 * Rendering an event with `new Date(...).toLocaleDateString()` reads the
 * instant in *the viewer's* zone, so the same event shows a different day to
 * someone in Los Angeles than to someone in Boston. That is the bug this
 * module exists to remove: every date here is resolved in an explicit zone.
 *
 * **Which field is the day.** Events carry both `eventDate` and `timeStart`,
 * and they disagree. `timeStart` is a true UTC instant built from the venue's
 * wall-clock time; `eventDate` is a legacy field that has drifted (across a
 * 119-event sample, `timeStart` matched the Salesforce opportunity's own date
 * 117 times against `eventDate`'s 105 — and where they differed, `timeStart`
 * was right). So `timeStart` read in the event's zone is the day, and
 * `eventDate` is only the fallback for records that have no `timeStart`.
 *
 * **What to show.** Legacy showed three things, and so should we: the event's
 * date, the time on the venue's clock ("3:00pm to 5:00pm EDT"), and the same
 * moment on the viewer's clock. The third one is what stops a facilitator in
 * Denver from reading a 3pm Boston event as 3pm their time.
 */

/** The venue timezone block Salesforce syncs onto an event. */
export interface EventVenueTimezone {
  /** IANA name, e.g. "America/Detroit". */
  readonly id?: string;
  /** Human name, e.g. "Eastern Daylight Time". Never an IANA zone. */
  readonly name?: string;
}

/**
 * The date/time fields of an event. Structural on purpose: event summaries,
 * event details and the smaller list DTOs all satisfy it to the extent they
 * carry the fields, and every function here degrades rather than throwing.
 */
export interface EventTimes {
  readonly eventDate?: string | null | undefined;
  /** The event's true start instant, in UTC. */
  readonly timeStart?: string | null | undefined;
  readonly timeEnd?: string | null | undefined;
  /** Wall-clock start at the venue, e.g. "3:00pm" — or "TBD". */
  readonly timeStartString?: string | null | undefined;
  readonly timeEndString?: string | null | undefined;
  /** Short zone label, e.g. "EDT". */
  readonly timeZone?: string | null | undefined;
  /** IANA zone, e.g. "America/New_York". */
  readonly longTimeZone?: string | null | undefined;
  readonly startVenue?:
    | { readonly timezone?: EventVenueTimezone | null | undefined }
    | null
    | undefined;
}

/**
 * The zone every date falls back to when an event carries none.
 *
 * Legacy's server-side `getEventDateAsMoment` defaulted here, so matching it
 * keeps un-zoned events reading exactly as they always have — and BCE's own
 * office is Eastern, which is the assumption staff already hold.
 */
const FALLBACK_ZONE = 'America/New_York';

/** Salesforce's "we don't know yet" marker, carried in the time strings. */
const TBD = 'TBD';

/** Whether a string names a zone this runtime can actually format in. */
function isUsableZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    // RangeError: a label like "EDT" or a zone this ICU build doesn't ship.
    return false;
  }
}

/**
 * The IANA zone the event happens in.
 *
 * Mirrors legacy's `Event.getTimeZone(event, true)`: the event's own
 * `longTimeZone` wins, then the venue's zone id. Both are validated, because
 * a bad zone makes `Intl` throw rather than fall back.
 *
 * @param event - The event.
 * @returns An IANA zone name. Never undefined — un-zoned events read as Eastern.
 */
export function eventZone(event: EventTimes | null | undefined): string {
  const candidates = [event?.longTimeZone, event?.startVenue?.timezone?.id];
  return candidates.find(isUsableZone) ?? FALLBACK_ZONE;
}

/** Whether the event names a zone of its own, rather than falling back. */
export function hasEventZone(event: EventTimes | null | undefined): boolean {
  return Boolean(event?.timeZone ?? event?.longTimeZone ?? event?.startVenue?.timezone?.id);
}

/**
 * The short zone label to print beside a time, e.g. "EDT".
 *
 * Mirrors legacy's `Event.getTimeZone(event)` — the synced abbreviation
 * first, then the venue's human zone name. Falls back to the IANA zone so
 * a time is never printed without saying which clock it is on.
 *
 * @param event - The event.
 * @returns A label suitable for display next to a time.
 */
export function eventZoneLabel(event: EventTimes | null | undefined): string {
  return event?.timeZone ?? event?.startVenue?.timezone?.name ?? eventZone(event);
}

/** Whether the value is a non-empty string, for the time-range builder. */
function isFilled(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** The viewer's own IANA zone, as the browser reports it. */
export function viewerZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_ZONE;
}

/** Whether the event's time strings say the time is not yet decided. */
export function isTimeTbd(event: EventTimes | null | undefined): boolean {
  return event?.timeStartString?.trim().toUpperCase() === TBD;
}

/** Parses an ISO instant, returning undefined rather than an Invalid Date. */
function instant(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/**
 * The moment the event starts, and the zone to read it in.
 *
 * @param event - The event.
 * @returns The instant plus its zone, or undefined when the event has neither
 *   a start instant nor a date.
 */
function dayInstant(event: EventTimes | null | undefined): Date | undefined {
  // timeStart is the event's real instant; eventDate is the legacy fallback.
  return instant(event?.timeStart) ?? instant(event?.eventDate);
}

/**
 * The event's calendar day as `yyyy-mm-dd`, in the event's own zone.
 *
 * This is the value to key, group, compare and link on — never
 * `eventDate.slice(0, 10)`, which is the UTC day of a legacy field.
 *
 * @param event - The event.
 * @returns The day, or undefined when the event has no date at all.
 */
export function eventDayIso(event: EventTimes | null | undefined): string | undefined {
  const at = dayInstant(event);
  if (!at) return undefined;
  // en-CA renders ISO-shaped yyyy-mm-dd, which is exactly the key we want.
  return new Intl.DateTimeFormat('en-CA', { timeZone: eventZone(event) }).format(at);
}

/**
 * The event's start as a sortable number — its real instant where there is
 * one, and the legacy date otherwise.
 *
 * Use this to order and to split upcoming from past, so an event that reads
 * as the 13th is not filed under the 12th by a drifted `eventDate`.
 *
 * @param event - The event.
 * @returns Milliseconds since the epoch, or undefined when it has no date.
 */
export function eventStartMs(event: EventTimes | null | undefined): number | undefined {
  return dayInstant(event)?.getTime();
}

/** How much of the date to spell out. */
export type EventDateStyle = 'full' | 'medium' | 'short';

const DATE_STYLE_OPTIONS: Readonly<Record<EventDateStyle, Intl.DateTimeFormatOptions>> = {
  /** "Thursday, August 13, 2026" — the detail page's headline date. */
  full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
  /** "Aug 13, 2026" — tables that show a whole date in one line. */
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  /** "Aug 13" — list cells that carry the year on a second line. */
  short: { month: 'short', day: 'numeric' },
};

/**
 * The event's date, written in the event's own zone.
 *
 * @param event - The event.
 * @param style - How much of the date to spell out. Defaults to `full`.
 * @returns The formatted date, or undefined when the event has no date.
 */
export function formatEventDate(
  event: EventTimes | null | undefined,
  style: EventDateStyle = 'full',
): string | undefined {
  const at = dayInstant(event);
  if (!at) return undefined;
  return new Intl.DateTimeFormat(undefined, {
    ...DATE_STYLE_OPTIONS[style],
    timeZone: eventZone(event),
  }).format(at);
}

/** The event's calendar year, in the event's zone. */
export function formatEventYear(event: EventTimes | null | undefined): string | undefined {
  const at = dayInstant(event);
  if (!at) return undefined;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    timeZone: eventZone(event),
  }).format(at);
}

/**
 * The time on the venue's clock, e.g. "3:00pm to 5:00pm EDT".
 *
 * Built from the synced wall-clock strings rather than by converting an
 * instant: those strings are what Salesforce holds and what everyone on site
 * agreed to, so they are reproduced verbatim.
 *
 * @param event - The event.
 * @param separator - What to put between start and end. Defaults to "to".
 * @returns The time range, "TBD", or undefined when no times are set.
 */
export function formatEventTime(
  event: EventTimes | null | undefined,
  separator = 'to',
): string | undefined {
  if (isTimeTbd(event)) return TBD;
  const range = [event?.timeStartString, event?.timeEndString]
    .filter(isFilled)
    .map((value) => value.trim())
    .join(` ${separator} `);
  if (!range) return undefined;
  const label = eventZoneLabel(event);
  return label ? `${range} ${label}` : range;
}

/**
 * The same moment on the viewer's clock — legacy's "Local Event Date/Time".
 *
 * Returns undefined when the event has no real start instant: without one
 * there is nothing honest to convert, and inventing a local time from the
 * wall-clock string would just restate it in the wrong zone.
 *
 * @param event - The event.
 * @param viewer - The zone to convert into. Defaults to the viewer's own,
 *   and is a parameter so the conversion stays testable without stubbing
 *   the platform's idea of "local".
 * @returns The viewer-local date and time range, plus their zone.
 */
export function formatViewerDateTime(
  event: EventTimes | null | undefined,
  viewer: string = viewerZone(),
): { readonly date: string; readonly time: string; readonly zone: string } | undefined {
  if (isTimeTbd(event)) return undefined;
  const start = instant(event?.timeStart);
  if (!start) return undefined;
  const end = instant(event?.timeEnd);
  const zone = isUsableZone(viewer) ? viewer : FALLBACK_ZONE;

  const date = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: zone,
  }).format(start);

  const clock = (at: Date): string =>
    new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: zone,
    }).format(at);

  // An end that lands on another day carries its own date, so a red-eye or a
  // zone-crossing event can't read as ending before it started.
  const endsOtherDay =
    end !== undefined &&
    new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(end) !==
      new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(start);
  const endText =
    end === undefined
      ? undefined
      : endsOtherDay
        ? `${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: zone }).format(end)}, ${clock(end)}`
        : clock(end);

  return {
    date,
    time: endText ? `${clock(start)} – ${endText}` : clock(start),
    zone,
  };
}

/**
 * Whether the viewer is already on the event's clock.
 *
 * Compared by actual offset at the event's own instant rather than by zone
 * name, so "America/Detroit" and "America/New_York" — the same clock — are
 * not reported as a difference the reader has to reconcile.
 *
 * @param event - The event.
 * @param viewer - The zone to compare against. Defaults to the viewer's own.
 * @returns True when both zones show the event at the same wall-clock time.
 */
export function viewerSharesEventClock(
  event: EventTimes | null | undefined,
  viewer: string = viewerZone(),
): boolean {
  const at = dayInstant(event);
  if (!at) return true;
  const inZone = (zone: string): string =>
    new Intl.DateTimeFormat('en-CA', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: zone,
    }).format(at);
  return inZone(eventZone(event)) === inZone(isUsableZone(viewer) ? viewer : FALLBACK_ZONE);
}
